import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUp, ArrowDown, Lock, Loader2, Clock, Trophy, XCircle,
  Search, X, ArrowDownUp, TrendingUp, TrendingDown, LayoutGrid,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Sparkline } from "@/components/Sparkline";
import { usePrices, fmtPrice } from "@/hooks/usePrices";
import { useAuth } from "@/context/AuthContext";
import { useDailyTrade, resetLabel, resultLabel } from "@/hooks/useDailyTrade";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GameEntryDialog } from "@/components/GameRules";


export default function Trade() {
  const { coins } = usePrices(15000);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { trade, unlocked, nextReset, placing, loading, place: submitTrade, schedule } = useDailyTrade(refresh);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("change_desc");
  const [entry, setEntry] = useState(null);

  const place = async (symbol, direction) => {
    if (await submitTrade(symbol, direction)) setEntry(null);
  };

  if (loading) {
    return (
      <div className="pb-24 grain min-h-screen">
        <Header title="Trade" />
        <div className="flex justify-center pt-24">
          <Loader2 className="animate-spin text-[#00E676]" />
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="pb-24 grain min-h-screen">
        <Header title="Trade" />
        <div className="max-w-md mx-auto px-4 pt-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#1E2330] flex items-center justify-center mx-auto mb-5">
            <Lock className="text-[#F59E0B]" size={28} />
          </div>
          <h2 className="font-display text-xl font-bold mb-2">Trading Locked</h2>
          <p className="text-sm text-slate-400 mb-6">
            You need a positive balance to unlock Call / Pull trading. Use your
            joining bonus, referral rewards, or make a deposit to get started.
          </p>
          <Button
            data-testid="goto-deposit-btn"
            onClick={() => navigate("/wallet")}
            className="h-12 px-8 bg-[#00E676] hover:bg-[#00C853] text-black font-bold rounded-xl"
          >
            Deposit Now
          </Button>
        </div>
      </div>
    );
  }

  const locked = !!trade;
  const q = query.trim().toLowerCase();
  let tradeableCoins = coins.filter((c) => c.symbol !== "USDT");
  if (q)
    tradeableCoins = tradeableCoins.filter(
      (c) => c.symbol.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
    );
  if (filter === "gainers") tradeableCoins = tradeableCoins.filter((c) => (c.change24h || 0) >= 0);
  if (filter === "losers") tradeableCoins = tradeableCoins.filter((c) => (c.change24h || 0) < 0);
  tradeableCoins = [...tradeableCoins].sort((a, b) => {
    if (sort === "change_desc") return (b.change24h || 0) - (a.change24h || 0);
    if (sort === "change_asc") return (a.change24h || 0) - (b.change24h || 0);
    if (sort === "price_desc") return (b.price || 0) - (a.price || 0);
    if (sort === "price_asc") return (a.price || 0) - (b.price || 0);
    return 0;
  });

  const sortLabels = {
    change_desc: "Top gainers",
    change_asc: "Top losers",
    price_desc: "Price ↓",
    price_asc: "Price ↑",
  };
  const cycleSort = () => {
    const order = ["change_desc", "change_asc", "price_desc", "price_asc"];
    setSort(order[(order.indexOf(sort) + 1) % order.length]);
  };
  const filterChips = [
    { key: "all", label: "All", icon: LayoutGrid },
    { key: "gainers", label: "Gainers", icon: TrendingUp },
    { key: "losers", label: "Losers", icon: TrendingDown },
  ];

  return (
    <div className="pb-24 grain min-h-screen">
      <Header title="Trade" />
      <div className="max-w-md mx-auto px-4 pt-4">
        {/* search + filters */}
        <div className="mb-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
            />
            <Input
              data-testid="trade-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search coin (BTC, Ethereum…)"
              className="h-11 pl-9 pr-9 bg-[#121721] border-[#1E293B] text-white text-sm rounded-xl"
            />
            {query && (
              <button
                data-testid="trade-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3">
            <div className="flex gap-1.5 flex-1 overflow-x-auto">
              {filterChips.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  data-testid={`trade-filter-${key}`}
                  onClick={() => setFilter(key)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                    filter === key
                      ? "border-[#00E676] bg-[#0E2E25] text-[#00E676]"
                      : "border-[#1E293B] bg-[#121721] text-slate-400"
                  }`}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            <button
              data-testid="trade-sort-toggle"
              onClick={cycleSort}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border border-[#1E293B] bg-[#121721] text-slate-300"
            >
              <ArrowDownUp size={13} /> {sortLabels[sort]}
            </button>
          </div>
        </div>

        {/* compact status banner */}
        {trade && trade.status === "pending" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="trade-pending-banner"
            className="mb-4 rounded-xl border border-[#1E293B] bg-[#121721] px-3 py-2.5 flex items-center gap-2"
          >
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${
                trade.direction === "pull"
                  ? "bg-[#0E2E25] text-[#00E676]"
                  : "bg-[#3A141A] text-[#FF3B30]"
              }`}
            >
              {trade.direction === "pull" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {trade.direction === "pull" ? "PULL" : "CALL"} · {trade.symbol}
            </span>
            <span className="text-xs text-slate-400">Results at {resultLabel(trade.resolve_at)}</span>
            <Loader2 className="animate-spin text-slate-500 ml-auto" size={14} />
          </motion.div>
        )}

        {trade && trade.status !== "pending" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="trade-result-banner"
            className={`mb-4 rounded-xl border px-3 py-3 flex items-center gap-3 ${
              trade.status === "won"
                ? "border-[#00E676]/40 bg-[#0E2E25]"
                : "border-[#FF3B30]/40 bg-[#3A141A]"
            }`}
          >
            {trade.status === "won" ? (
              <Trophy className="text-[#00E676]" size={22} />
            ) : (
              <XCircle className="text-[#FF3B30]" size={22} />
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold">
                {trade.status === "won" ? "You won today" : "You lost today"}{" "}
                <span
                  className={`font-mono-num ${
                    trade.status === "won" ? "text-[#00E676]" : "text-[#FF3B30]"
                  }`}
                >
                  {trade.payout > 0 ? "+" : ""}
                  {trade.payout} USDT
                </span>
              </div>
              <div className="text-[11px] text-slate-400 flex items-center gap-1">
                <Clock size={11} /> Next bid opens {resetLabel(nextReset)}
              </div>
            </div>
          </motion.div>
        )}

        {!trade && (
          <div className="mb-4 rounded-xl border border-[#1E293B] bg-[#121721] px-3 py-2.5 text-xs text-slate-400">
            Pick <span className="text-[#00E676] font-semibold">PULL</span> (up) or{" "}
            <span className="text-[#FF3B30] font-semibold">CALL</span> (down) on any one asset. 1
            trade per day · resets 5:00 AM IST. Results at {resultLabel(schedule?.next_result_at)}.
          </div>
        )}

        {/* coin list with inline graph + buttons */}
        <div className="space-y-2.5">
          {tradeableCoins.length === 0 && (
            <div
              data-testid="trade-empty-state"
              className="text-center py-12 text-slate-500 text-sm"
            >
              <Search size={28} className="mx-auto mb-3 opacity-50" />
              No coins match your search or filter.
            </div>
          )}
          {tradeableCoins.map((c, i) => {
            const up = (c.change24h || 0) >= 0;
            const isMine = trade && trade.symbol === c.symbol;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`trade-coin-${c.symbol.toLowerCase()}`}
                className={`rounded-2xl border p-3 bg-[#121721] transition-colors ${
                  isMine ? "border-[#00E676]/50" : "border-[#1E293B]"
                }`}
              >
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => navigate(`/coin/${c.symbol}`)}
                >
                  <div className="w-8 h-8 rounded-full bg-[#1E2330] flex items-center justify-center overflow-hidden shrink-0">
                    {c.image ? (
                      <img src={c.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-bold text-slate-300">
                        {c.symbol.slice(0, 3)}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm leading-tight">{c.symbol}</div>
                    <div
                      className={`font-mono-num text-[11px] ${
                        up ? "text-[#00E676]" : "text-[#FF3B30]"
                      }`}
                    >
                      {up ? "+" : ""}
                      {(c.change24h || 0).toFixed(2)}%
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    <Sparkline data={c.sparkline} up={up} width={60} height={26} />
                    <div className="font-mono-num text-sm font-semibold w-[86px] text-right">
                      ${fmtPrice(c.price)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    data-testid={`pull-${c.symbol.toLowerCase()}`}
                    disabled={locked || placing !== ""}
                    onClick={() => setEntry({ symbol: c.symbol, direction: "pull" })}
                    className={`h-11 font-display font-bold rounded-xl gap-1 ${
                      isMine && trade.direction === "pull"
                        ? "bg-[#00E676] text-black"
                        : "bg-[#0E2E25] text-[#00E676] hover:bg-[#00E676] hover:text-black"
                    } disabled:opacity-40`}
                  >
                    {placing === c.symbol + "pull" ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <>
                        <ArrowUp size={16} strokeWidth={3} /> PULL
                      </>
                    )}
                  </Button>
                  <Button
                    data-testid={`call-${c.symbol.toLowerCase()}`}
                    disabled={locked || placing !== ""}
                    onClick={() => setEntry({ symbol: c.symbol, direction: "call" })}
                    className={`h-11 font-display font-bold rounded-xl gap-1 ${
                      isMine && trade.direction === "call"
                        ? "bg-[#FF3B30] text-white"
                        : "bg-[#3A141A] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
                    } disabled:opacity-40`}
                  >
                    {placing === c.symbol + "call" ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <>
                        <ArrowDown size={16} strokeWidth={3} /> CALL
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <GameEntryDialog
        entry={entry}
        onClose={() => setEntry(null)}
        onConfirm={() => place(entry.symbol, entry.direction)}
        busy={placing !== ""}
        resultAt={schedule?.next_result_at}
      />
    </div>
  );
}
